import React, { useEffect, useState, useCallback } from 'react';
import { DatasetStatistic, Dataset } from '../../../types/dataset';
import { AlgorithmVersion } from '../../../types';
import { AlgorithmStatistic } from '../../../types/result';
import { Bar } from 'react-chartjs-2';

type Props = {
  selectedDataset: null | Dataset;
  selectedDatasetStatistics: null | DatasetStatistic;
  statistics: DatasetStatistic[];
  compareVersionId: number | null;
  algorithmVersions: AlgorithmVersion[];
};

export const ChartScorePerField = ({
  statistics,
  selectedDataset,
  selectedDatasetStatistics,
  compareVersionId,
  algorithmVersions,
}: Props) => {
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [latestValues, setLatestValues] = useState<number[]>([]);
  const [previousValues, setPreviousValues] = useState<number[]>([]);
  const [chartDatasets, setChartDatasets] = useState<any[]>([]);

  const calculateAverages = (labels: string[], array: AlgorithmStatistic[], setFunction: any) => {
    labels.forEach(label => {
      const forAvg = array.reduce(
        (total: number, next: any) =>
          total + (next.summary.labels.find((l: any) => l.name === label)?.statistics.accuracy || 0),
        0
      );
      const countOccurences = array.filter(stat => stat.summary.labels.find((l: any) => l.name === label)).length;
      const avg = (forAvg * 100) / countOccurences;
      setFunction((prev: any) => [...prev, avg]);
    });
  };

  const calculateValues = useCallback(
    (labels: string[], id: number, setFunction: any) => {
      labels.forEach(label => {
        const newValue =
          selectedDatasetStatistics?.stats
            .find(stat => parseInt(stat.algorithmVersion.id) === +id)
            ?.summary.labels.find((l: any) => l.name === label)?.statistics.accuracy || 0;
        setFunction((prev: any) => [...prev, newValue * 100]);
      });
    },
    [selectedDatasetStatistics]
  );

  const determineStatsForAverageView = useCallback(
    (compareVersionId: number | null) => {
      const latest = algorithmVersions[0];
      const statsForLabels = statistics.map(stat => {
        const filteredStats = stat.stats.filter((algorithmStat: AlgorithmStatistic) =>
          compareVersionId
            ? algorithmStat.algorithmVersion.id === latest.id ||
              parseInt(algorithmStat.algorithmVersion.id) === +compareVersionId
            : algorithmStat.algorithmVersion.id === latest.id
        );
        return filteredStats;
      });
      const stats: AlgorithmStatistic[] = [];
      statsForLabels.forEach(el => stats.push(...el));
      return stats;
    },
    [algorithmVersions, statistics]
  );

  //LABELS
  useEffect(() => {
    if (!selectedDataset) {
      setChartLabels([]);
      const stats = determineStatsForAverageView(compareVersionId);
      stats.forEach(stat => {
        stat.summary.labels.forEach(label => {
          setChartLabels(prev => (!prev.includes(label.name) ? [...prev, label.name] : prev));
        });
      });
    }
    if (selectedDataset && selectedDatasetStatistics?.stats.length) {
      setChartLabels([]);
      selectedDatasetStatistics.stats[selectedDatasetStatistics.stats.length - 1].summary.labels.forEach(label => {
        setChartLabels(prev => [...prev, label.name]);
      });
      if (compareVersionId && selectedDatasetStatistics.stats.length > 1) {
        const compareVersionStats = selectedDatasetStatistics.stats.find(
          stat => parseInt(stat.algorithmVersion.id) === +compareVersionId
        );
        compareVersionStats?.summary.labels.forEach(label => {
          setChartLabels(prev => (!prev.includes(label.name) ? [...prev, label.name] : prev));
        });
      }
    }
  }, [
    selectedDataset,
    selectedDatasetStatistics,
    compareVersionId,
    statistics,
    algorithmVersions,
    determineStatsForAverageView,
  ]);

  //VALUES
  useEffect(() => {
    if (!selectedDataset) {
      clearGraph();
      const latest = algorithmVersions[0];
      const stats = determineStatsForAverageView(compareVersionId);
      const previousValues = compareVersionId
        ? stats.filter(stat => parseInt(stat.algorithmVersion.id) === +compareVersionId)
        : [];
      const latestValues = stats.filter(stat => stat.algorithmVersion.id === latest.id);
      calculateAverages(chartLabels, latestValues, setLatestValues);
      if (compareVersionId && previousValues.length) {
        calculateAverages(chartLabels, previousValues, setPreviousValues);
      }
    }
    if (selectedDataset && selectedDatasetStatistics?.stats.length) {
      clearGraph();
      calculateValues(chartLabels, parseInt(selectedDatasetStatistics.stats[0].algorithmVersion.id), setLatestValues);
      if (compareVersionId && selectedDatasetStatistics.stats.length > 1) {
        calculateValues(chartLabels, compareVersionId, setPreviousValues);
      }
    }
  }, [
    chartLabels,
    compareVersionId,
    selectedDataset,
    selectedDatasetStatistics,
    calculateValues,
    statistics,
    algorithmVersions,
    determineStatsForAverageView,
  ]);

  const clearGraph = () => {
    setLatestValues([]);
    setPreviousValues([]);
  };

  //CHART DATASETS
  useEffect(() => {
    if (latestValues.length) {
      setChartDatasets([]);
      if (previousValues.length) {
        const dataset = {
          label: 'Compared score',
          data: previousValues,
          backgroundColor: 'rgba(48, 22, 154, 0.4)',
        };
        setChartDatasets(prev => [...prev, dataset]);
      }
      let dataset = {
        label: `Latest Score`,
        data: latestValues,
        backgroundColor: 'rgba(54, 162, 235, 0.4)',
      };
      setChartDatasets(prev => [...prev, dataset]);
    }
  }, [latestValues, previousValues]);

  const data = {
    labels: chartLabels,
    datasets: chartDatasets,
  };

  const options = {
    indexAxis: 'x',
    maintainAspectRatio: false,
    scales: {
      y: {
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Score Per Field',
        font: { size: '16px' },
      },
    },
  };

  return (
    <>
      <Bar data={data} options={options} redraw={false} style={{ height: 350 }} />
    </>
  );
};
